/**
 * Register OneSignal subscription IDs with the Cockpit Django backend.
 * Shared by web and native implementations.
 */

import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import { useAuthStore } from '@/stores/authStore';
import {
  markBackendSubscriptionRegistered,
  wasBackendSubscriptionRegistered,
} from '@/services/oneSignal/shared';

export type PushSubscriptionPlatform = 'web' | 'android' | 'ios';

const registerInFlight = new Map<string, Promise<void>>();

function getNativeDeviceId(): string {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent.slice(0, 255);
}

export async function registerPushSubscriptionWithBackend(
  subscriptionId: string | null | undefined,
  platform: PushSubscriptionPlatform,
): Promise<void> {
  const id = subscriptionId?.trim();
  if (!id) return;

  console.log('[OneSignal] Subscription detected', { subscriptionId: id, platform });

  if (!useAuthStore.getState().isAuthenticated) {
    return;
  }

  if (wasBackendSubscriptionRegistered(id)) {
    console.log('[OneSignal] Subscription already registered', {
      subscriptionId: id,
      platform,
    });
    return;
  }

  const existing = registerInFlight.get(id);
  if (existing) {
    await existing;
    return;
  }

  const run = (async () => {
    const payload = {
      onesignal_subscription_id: id,
      platform,
      device_id: getNativeDeviceId(),
      is_active: true,
    };

    console.log('[OneSignal] Registering subscription', {
      subscriptionId: id,
      platform,
    });

    const postOnce = () =>
      apiClient.post(API_ENDPOINTS.NOTIFICATIONS.PUSH_SUBSCRIPTIONS_REGISTER, payload);

    try {
      try {
        await postOnce();
      } catch (firstError) {
        console.warn('[OneSignal] Registration attempt failed, retrying once:', firstError);
        await postOnce();
      }

      markBackendSubscriptionRegistered(id);
      console.log('[OneSignal] Registration successful', {
        subscriptionId: id,
        platform,
      });
    } catch (error) {
      console.error('[OneSignal] Registration failed', {
        subscriptionId: id,
        platform,
        error,
      });
    }
  })();

  registerInFlight.set(id, run);
  try {
    await run;
  } finally {
    registerInFlight.delete(id);
  }
}

export async function deactivatePushSubscriptionOnBackend(
  subscriptionId: string | null,
): Promise<void> {
  if (!subscriptionId) return;

  try {
    await apiClient.delete(API_ENDPOINTS.NOTIFICATIONS.PUSH_SUBSCRIPTIONS_REGISTER, {
      data: { onesignal_subscription_id: subscriptionId },
    });
    console.log('[OneSignal] Backend subscription deactivated', {
      subscriptionId,
    });
  } catch (error) {
    console.warn('[OneSignal] Backend subscription deactivate failed:', error);
  }
}
